import React, { useEffect, useState } from "react";
import {
  X,
  Printer,
  Send,
  LogOut,
  DollarSign,
  Wallet,
  Loader2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import {
  getShiftSummary,
  getShiftOrders,
  closeShift,
} from "../../../api/shiftService";
import { executePrint, getLoggedInUserName } from "../../../utils/printManager";

function fmt(n) {
  return "Rp " + (n ?? 0).toLocaleString("id-ID");
}

function parseCurrencyInput(value) {
  if (value === null || value === undefined) return 0;
  const numeric = String(value).replace(/[^\d]/g, "");
  return numeric ? Number(numeric) : 0;
}

function formatCurrencyInput(value) {
  const numeric = parseCurrencyInput(value);
  return numeric ? numeric.toLocaleString("id-ID") : "";
}

const METHOD_LABEL = {
  CASH: "Tunai",
  QRIS: "QRIS",
  TRANSFER: "Transfer",
  MIXED: "Campuran",
};

export default function CloseShiftModal({ onClose, shiftId, shift }) {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [pendingCount, setPendingCount] = useState(null);
  const [closingCash, setClosingCash] = useState("");
  const [notes, setNotes] = useState("");
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState(null);
  const closingCashValue = parseCurrencyInput(closingCash);

  useEffect(() => {
    if (!shiftId) return;
    const extractTotal = (res) => {
      const metaTotal = Number(res?.meta?.total ?? res?.meta?.total_items);
      if (Number.isFinite(metaTotal)) return metaTotal;
      return Array.isArray(res?.data) ? res.data.length : 0;
    };

    setLoading(true);
    setSummaryError(null);
    setPendingCount(null);
    Promise.all([
      getShiftSummary(shiftId),
      getShiftOrders(shiftId, { page: 1, limit: 1, status: "PENDING" }).catch(
        () => null,
      ),
    ])
      .then(([summaryRes, pendingRes]) => {
        setSummary(summaryRes?.data ?? summaryRes);
        if (pendingRes) {
          setPendingCount(extractTotal(pendingRes));
        }
      })
      .catch((err) =>
        setSummaryError(err.message || "Gagal memuat ringkasan shift"),
      )
      .finally(() => setLoading(false));
  }, [shiftId]);

  const handleClose = async (action) => {
    if (!closingCash || closingCash.trim() === "") {
      setCloseError("Jumlah disetorkan wajib diisi");
      return;
    }

    try {
      setClosing(true);
      setCloseError(null);
      await closeShift(shiftId, { closing_cash: closingCashValue, notes });
      if (action === "print") {
        const shiftData = {
          storeName: localStorage.getItem("tenantName"),
          storeAddress: localStorage.getItem("tenantAddress"),
          storePhone: localStorage.getItem("tenantPhone"),
          storeLogoUrl: "", // Dihilangkan sesuai permintaan user
          shift: {
            ...shift,
            ...summary,
            summary: summary,
            closed_at: new Date().toISOString(),
            closing_cash: closingCashValue,
            notes: notes,
            closed_by_name: getLoggedInUserName(),
          },
        };
        try {
          await executePrint(shiftData, "shift");
        } catch (printErr) {
          console.error("Failed to print shift receipt:", printErr);
        }
      }
      onClose(true); // true = closed successfully
    } catch (err) {
      setCloseError(err.message || "Gagal menutup shift");
    } finally {
      setClosing(false);
    }
  };

  const formatDate = (iso) =>
    iso
      ? new Date(iso).toLocaleString("id-ID", {
          weekday: "long",
          day: "2-digit",
          month: "long",
          year: "numeric",
        })
      : "-";
  const formatTime = (iso) =>
    iso
      ? new Date(iso).toLocaleTimeString("id-ID", {
          hour: "2-digit",
          minute: "2-digit",
        }) + " WIB"
      : "-";
  const pendingTxnCount = Number(
    pendingCount ?? summary?.total_transaction_pending ?? 0,
  );

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm p-4 overflow-y-auto modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl w-full max-w-[1000px] shadow-2xl flex overflow-hidden h-[85vh] modal-panel-enter">
        {/* Left: Summary */}
        <div className="w-1/2 border-r p-8 flex flex-col bg-white overflow-y-auto">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Rekap Penjualan Kasir
            </h2>
            <p className="text-sm text-gray-500">
              Ringkasan informasi transaksi shift kasir saat ini
            </p>
          </div>

          {loadingSummary && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-7 h-7 animate-spin text-teal-600" />
              <p className="text-sm text-gray-500">Memuat ringkasan...</p>
            </div>
          )}

          {!loadingSummary && summaryError && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
              <AlertCircle className="w-7 h-7 text-red-500" />
              <p className="text-sm text-red-600">{summaryError}</p>
            </div>
          )}

          {!loadingSummary && !summaryError && summary && (
            <div className="space-y-6 flex-1">
              {/* Modal Awal */}
              <div className="flex justify-between items-center py-2 border-b border-dashed">
                <div className="flex items-center gap-3 text-gray-600">
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Modal Awal</span>
                </div>
                <span className="font-bold text-gray-900">
                  {fmt(shift?.opening_cash || summary?.opening_cash || shift?.start_cash || 0)}
                </span>
              </div>

              {/* Revenue by method */}
              <div>
                <div className="flex justify-between items-center py-2 border-b border-dashed">
                  <div className="flex items-center gap-3 text-gray-600">
                    <DollarSign className="w-5 h-5" />
                    <span className="font-medium">Total Penjualan</span>
                  </div>
                  <span className="font-bold text-teal-600">
                    {fmt(
                      (summary.revenue_by_payment_method ?? []).reduce(
                        (s, m) => s + Number(m.total || 0),
                        0,
                      ),
                    )}
                  </span>
                </div>
                <div className="pl-8 space-y-3 text-sm mt-3">
                  {(summary.revenue_by_payment_method ?? []).map((m) => (
                    <div key={m.method} className="flex justify-between">
                      <span className="text-gray-500">
                        • {METHOD_LABEL[m.method] ?? m.method}{m.count ? ` (${m.count})` : ''}
                      </span>
                      <span className="font-medium text-gray-800">
                        {fmt(m.total)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Kas Keluar */}
              <div className="flex justify-between items-center py-2 border-b border-dashed text-red-600">
                <div className="flex items-center gap-3">
                  <LogOut className="w-5 h-5 rotate-180" />
                  <span className="font-medium">Total Kas Keluar</span>
                </div>
                <span className="font-bold">{fmt(summary.total_cash_out)}</span>
              </div>

              {/* Transaction counts */}
              <div className="space-y-3 py-2 border-b border-dashed">
                <div className="flex justify-between text-sm">
                  <span className="font-bold text-gray-700">
                    Total Transaksi
                  </span>
                  <span className="font-bold text-gray-900">
                    {Number(
                      summary.total_transaction_receipt || 0,
                    ).toLocaleString("id-ID")}{" "}
                    Nota
                  </span>
                </div>
                {pendingTxnCount > 0 && (
                  <div className="flex justify-between text-sm text-orange-600">
                    <span>Transaksi Pending</span>
                    <span>{pendingTxnCount.toLocaleString("id-ID")} Nota</span>
                  </div>
                )}
                {summary.total_transaction_cancelled > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Transaksi Dibatalkan</span>
                    <span>
                      {Number(
                        summary.total_transaction_cancelled || 0,
                      ).toLocaleString("id-ID")}{" "}
                      Nota
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-green-600">
                  <span>Transaksi Selesai</span>
                  <span>
                    {Number(summary.total_transaction_done || 0).toLocaleString(
                      "id-ID",
                    )}{" "}
                    Nota
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Total Kas Kasir */}
          <div className="bg-orange-50 rounded-xl p-4 mt-6 flex justify-between items-center">
            <div className="flex items-center gap-3 text-gray-800">
              <Wallet className="w-6 h-6" />
              <span className="font-bold">Total Kas Kasir</span>
            </div>
            <span className="text-xl font-bold text-orange-600">
              {summary ? fmt(summary.total_cashier_cash) : "-"}
            </span>
          </div>
        </div>

        {/* Right: Close Form */}
        <div className="w-1/2 p-8 flex flex-col bg-gray-50 overflow-y-auto">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Tutup Kasir</h2>
              <p className="text-sm text-gray-500">
                Menutup shift kasir untuk disetorkan
              </p>
            </div>
            <button
              onClick={() => onClose(false)}
              className="p-2 bg-white rounded-full shadow-sm hover:bg-gray-100">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="space-y-4 mb-8 text-sm">
            <div className="flex justify-between py-2 border-b border-dashed border-gray-300">
              <span className="text-gray-500">Dibuka oleh</span>
              <span className="font-bold text-gray-900">
                {shift?.opened_by_name || shift?.opened_by || "-"}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-dashed border-gray-300">
              <span className="text-gray-500">Tanggal Buka</span>
              <span className="font-bold text-gray-900">
                {formatDate(shift?.opened_at)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-dashed border-gray-300">
              <span className="text-gray-500">Waktu Buka</span>
              <span className="font-bold text-gray-900">
                {formatTime(shift?.opened_at)}
              </span>
            </div>
          </div>

          <div className="space-y-6 flex-1">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Jumlah Disetorkan <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  Rp
                </span>
                <input
                  type="text"
                  value={closingCash}
                  onChange={(e) =>
                    setClosingCash(formatCurrencyInput(e.target.value))
                  }
                  className="w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                  placeholder="0"
                  inputMode="numeric"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (summary) setClosingCash(formatCurrencyInput(summary.total_cashier_cash));
                  }}
                  title="Auto isi sesuai sistem"
                  className="absolute right-0 top-0 bottom-0 px-3 bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors border border-l-0 border-gray-200 rounded-r-lg flex items-center justify-center"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Sisa Uang Kas Kasir
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  Rp
                </span>
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                  placeholder="0"
                  readOnly
                  value={
                    summary
                      ? Math.max(
                          0,
                          (summary.total_cashier_cash ?? 0) - closingCashValue,
                        ).toLocaleString("id-ID")
                      : ""
                  }
                />
              </div>
            </div>

            <div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full h-32 p-4 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none bg-white"
                placeholder="Tambahkan catatan disini apabila diperlukan..."
              />
            </div>
          </div>

          {closeError && (
            <p className="text-sm text-red-600 mt-3">{closeError}</p>
          )}

          <div className="mt-8 space-y-3">
            <div className="flex gap-3">
              <button
                onClick={() => handleClose("send")}
                disabled={closing}
                className="flex-1 py-3 bg-teal-50 text-teal-700 rounded-lg font-bold hover:bg-teal-100 flex items-center justify-center gap-2 disabled:opacity-50">
                {closing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Tutup & Kirim
              </button>
              <button
                onClick={() => handleClose("print")}
                disabled={closing}
                className="flex-1 py-3 bg-orange-50 text-orange-700 rounded-lg font-bold hover:bg-orange-100 flex items-center justify-center gap-2 disabled:opacity-50">
                {closing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Printer className="w-4 h-4" />
                )}
                Tutup & Cetak
              </button>
            </div>
            <button
              onClick={() => handleClose("now")}
              disabled={closing}
              className="w-full py-3 bg-gray-200 text-gray-600 rounded-lg font-bold hover:bg-gray-300 flex items-center justify-center gap-2 disabled:opacity-50">
              {closing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <X className="w-4 h-4" />
              )}
              Tutup Kasir Sekarang
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
